import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import axios from 'axios';
import { AlertCircle, CheckCircle2, MessageSquare, RefreshCw, Send, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import StaffPageHeader from '../../components/StaffPageHeader';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

const CATEGORY_TABS = [
  { key: 'registration', label: 'Pelanggan' },
  { key: 'billing', label: 'Tagihan' },
  { key: 'service', label: 'Layanan' },
  { key: 'support', label: 'Dukungan' },
  { key: 'hotspot', label: 'Hotspot' },
  { key: 'retention', label: 'Retensi' },
];

const FALLBACK_TRIGGER_EVENT_OPTIONS = [
  { key: 'customer_registration_received', label: 'Pengajuan pelanggan diterima', category: 'registration' },
  { key: 'customer_registration_approved', label: 'Pengajuan pelanggan disetujui', category: 'registration' },
  { key: 'billing_invoice_created', label: 'Tagihan baru terbit', category: 'billing' },
  { key: 'billing_due_reminder', label: 'Pengingat jatuh tempo tagihan', category: 'billing' },
  { key: 'billing_overdue_notice', label: 'Pemberitahuan tagihan lewat jatuh tempo', category: 'billing' },
  { key: 'billing_payment_received', label: 'Pembayaran tagihan diterima', category: 'billing' },
  { key: 'service_renewal_success', label: 'Perpanjangan layanan berhasil', category: 'service' },
  { key: 'support_ticket_created', label: 'Tiket dukungan dibuat', category: 'support' },
  { key: 'support_area_incident_warning', label: 'Peringatan gangguan area', category: 'support' },
  { key: 'support_service_restored', label: 'Layanan area pulih kembali', category: 'support' },
];

const VARIABLE_HELPERS = [
  'customer_name',
  'customer_code',
  'invoice_number',
  'invoice_amount',
  'due_date',
  'payment_status',
  'service_package',
  'service_active_until',
  'ticket_number',
  'ticket_status',
  'area_name',
  'incident_summary',
  'company_name',
  'support_number',
];

const SAMPLE_CONTEXT = {
  customer_name: 'Pelanggan Uji',
  customer_code: 'CUST-0001',
  invoice_number: 'INV-2026-001',
  invoice_amount: '150.000',
  due_date: '2026-03-31',
  payment_status: 'Belum Bayar',
  service_package: 'Internet 20 Mbps',
  service_active_until: '2026-04-30',
  ticket_number: 'TCK-2026-001',
  ticket_status: 'Diproses',
  area_name: 'Area Matakali',
  incident_summary: 'Gangguan sementara sedang ditangani',
  company_name: 'Kedai Teknologi Celebes',
  support_number: '0812-0000-0000',
};

const blankForm = {
  id: null,
  template_key: '',
  template_name: '',
  category: 'support',
  trigger_event: '',
  channel: 'whatsapp_business',
  content: '',
  allowed_variables: ['customer_name', 'company_name', 'support_number'],
  is_active: true,
  requires_approval: false,
  is_system_default: false,
  is_editable: true,
  sort_order: 0,
  notes: '',
};

const renderPreview = (content, context) => {
  if (!content) return '';
  return String(content).replace(/\{([a-zA-Z0-9_]+)\}/g, (_, key) => {
    if (Object.prototype.hasOwnProperty.call(context, key) && context[key] != null) {
      return String(context[key]);
    }
    return '';
  });
};

const statusChipClass = (isActive) => (
  isActive
    ? 'border-emerald-400/45 bg-emerald-500/15 text-emerald-200'
    : 'border-slate-500/45 bg-slate-600/15 text-slate-200'
);

export default function AdminWhatsAppTemplates() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processingOutbound, setProcessingOutbound] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [pendingOutboundItems, setPendingOutboundItems] = useState([]);
  const [groupedOutboundItems, setGroupedOutboundItems] = useState([]);
  const [pendingFilterEvent, setPendingFilterEvent] = useState('all');
  const [groupFilterEvent, setGroupFilterEvent] = useState('all');
  const [groupFilterApprovalStatus, setGroupFilterApprovalStatus] = useState('all');
  const [groupSearch, setGroupSearch] = useState('');
  const [groupSort, setGroupSort] = useState('urgency');
  const [groupPendingOnly, setGroupPendingOnly] = useState(false);
  const [inboxView, setInboxView] = useState('grouped');
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(5);
  const [actingMessageId, setActingMessageId] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const [actingGroupId, setActingGroupId] = useState(null);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [groupRecipientsById, setGroupRecipientsById] = useState({});
  const [approvalSummary, setApprovalSummary] = useState(null);
  const [activeCategory, setActiveCategory] = useState('registration');
  const [form, setForm] = useState(blankForm);
  const [selectedId, setSelectedId] = useState(null);
  const [testNumber, setTestNumber] = useState('081234567890');
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState('');
  const [triggerEventOptions, setTriggerEventOptions] = useState(FALLBACK_TRIGGER_EVENT_OPTIONS);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = getHeaders();
      const [templatesRes, triggerEventRes, summaryRes, pendingRes, groupedRes] = await Promise.all([
        axios.get(`${API}/whatsapp/templates`, { headers }),
        axios.get(`${API}/whatsapp/templates/trigger-events`, { headers }).catch(() => ({ data: FALLBACK_TRIGGER_EVENT_OPTIONS })),
        axios.get(`${API}/whatsapp/outbound-approval-summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/whatsapp/outbound?approval_status=pending&status=draft&limit=15`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/whatsapp/outbound/inbox?view=grouped&limit=30`, { headers }).catch(() => ({ data: { items: [] } })),
      ]);
      const rows = Array.isArray(templatesRes?.data) ? templatesRes.data : [];
      const triggerRows = Array.isArray(triggerEventRes?.data) && triggerEventRes.data.length > 0
        ? triggerEventRes.data
        : FALLBACK_TRIGGER_EVENT_OPTIONS;
      setApprovalSummary(summaryRes?.data && typeof summaryRes.data === 'object' ? summaryRes.data : null);
      setPendingOutboundItems(Array.isArray(pendingRes?.data) ? pendingRes.data : []);
      setGroupedOutboundItems(Array.isArray(groupedRes?.data?.items) ? groupedRes.data.items : []);
      setTriggerEventOptions(triggerRows);
      setTemplates(rows);
      setLastUpdatedAt(new Date());
      if (!selectedId && rows.length > 0) {
        setSelectedId(rows[0].id);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal memuat template');
    } finally {
      setLoading(false);
    }
  }, [getHeaders, selectedId]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => {
      loadTemplates();
    }, 30000);
    return () => clearInterval(timer);
  }, [autoRefresh, loadTemplates]);

  const filteredTemplates = useMemo(
    () => templates.filter((row) => String(row.category || '').toLowerCase() === activeCategory),
    [templates, activeCategory],
  );

  const selectedTemplate = useMemo(
    () => templates.find((row) => row.id === selectedId) || null,
    [templates, selectedId],
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    const allowed = Array.isArray(selectedTemplate?.variables_json?.allowed_variables)
      ? selectedTemplate.variables_json.allowed_variables
      : [];
    setForm({
      id: selectedTemplate.id,
      template_key: selectedTemplate.template_key || '',
      template_name: selectedTemplate.template_name || '',
      category: selectedTemplate.category || 'support',
      trigger_event: selectedTemplate.trigger_event || '',
      channel: selectedTemplate.channel || 'whatsapp_business',
      content: selectedTemplate.content || '',
      allowed_variables: allowed.length > 0 ? allowed : ['customer_name', 'company_name'],
      is_active: Boolean(selectedTemplate.is_active),
      requires_approval: Boolean(selectedTemplate.requires_approval),
      is_system_default: Boolean(selectedTemplate.is_system_default),
      is_editable: selectedTemplate.is_editable !== false,
      sort_order: Number(selectedTemplate.sort_order || 0),
      notes: selectedTemplate.notes || '',
    });
    setValidationResult(null);
  }, [selectedTemplate]);

  const onSelectTemplate = (row) => {
    setSelectedId(row.id);
    setActiveCategory(String(row.category || '').toLowerCase());
  };

  const onCreateNew = () => {
    setSelectedId(null);
    setForm({ ...blankForm, category: activeCategory });
    setValidationResult(null);
  };

  const insertVariable = (key) => {
    setForm((prev) => ({ ...prev, content: `${prev.content}{${key}}` }));
  };

  const saveTemplate = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        template_key: form.template_key.trim(),
        template_name: form.template_name.trim(),
        category: form.category,
        trigger_event: form.trigger_event.trim(),
        channel: form.channel,
        content: form.content,
        variables_json: {
          allowed_variables: form.allowed_variables,
          sample_context: SAMPLE_CONTEXT,
        },
        is_active: form.is_active,
        requires_approval: form.requires_approval,
        sort_order: Number(form.sort_order || 0),
        notes: form.notes || null,
      };
      if (!form.id) {
        await axios.post(`${API}/whatsapp/templates`, payload, { headers: getHeaders() });
        toast.success('Template berhasil dibuat');
      } else {
        await axios.put(`${API}/whatsapp/templates/${form.id}`, payload, { headers: getHeaders() });
        toast.success('Template berhasil diperbarui');
      }
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal menyimpan template');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row, active) => {
    try {
      const path = active ? 'activate' : 'deactivate';
      await axios.post(`${API}/whatsapp/templates/${row.id}/${path}`, {}, { headers: getHeaders() });
      toast.success(active ? 'Template diaktifkan' : 'Template dinonaktifkan');
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal mengubah status template');
    }
  };

  const validateTemplate = async () => {
    try {
      const { data } = await axios.post(
        `${API}/whatsapp/templates/${form.id || 0}/validate`,
        {
          content: form.content,
          allowed_variables: form.allowed_variables,
        },
        { headers: getHeaders() },
      );
      setValidationResult(data);
      toast.success(data.valid ? 'Validasi template valid' : 'Ada variabel yang perlu diperbaiki');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal validasi template');
    }
  };

  const testSend = async () => {
    if (!form.id) {
      toast.error('Simpan template dulu sebelum uji kirim');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/whatsapp/templates/${form.id}/test-send`,
        {
          target_number: testNumber,
          context: SAMPLE_CONTEXT,
        },
        { headers: getHeaders() },
      );
      toast.success(`Uji kirim: ${data.send_status}`);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Uji kirim gagal');
    }
  };

  const resetDefault = async () => {
    if (!form.id) return;
    try {
      await axios.post(`${API}/whatsapp/templates/${form.id}/reset-default`, {}, { headers: getHeaders() });
      toast.success('Template dikembalikan ke default sistem');
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal reset template');
    }
  };

  const previewText = useMemo(() => renderPreview(form.content, SAMPLE_CONTEXT), [form.content]);
  const getTriggerEventLabel = useCallback((eventKey) => {
    const key = String(eventKey || '').trim();
    const found = triggerEventOptions.find((item) => item.key === key);
    return found?.label || key || '-';
  }, [triggerEventOptions]);

  const getRowTriggerEventLabel = useCallback((row) => {
    if (row && typeof row.trigger_event_label === 'string' && row.trigger_event_label.trim()) {
      return row.trigger_event_label.trim();
    }
    return getTriggerEventLabel(row?.trigger_event);
  }, [getTriggerEventLabel]);

  const triggerEventOptionsByCategory = useMemo(
    () => triggerEventOptions.filter((item) => item.category === form.category),
    [form.category, triggerEventOptions],
  );

  const summaryCards = useMemo(() => ([
    { key: 'pending_total', label: 'Menunggu Persetujuan', value: Number(approvalSummary?.pending_total || 0), tone: 'border-amber-400/40 bg-amber-500/10 text-amber-100' },
    { key: 'draft_total', label: 'Draft', value: Number(approvalSummary?.draft_total || 0), tone: 'border-slate-400/40 bg-slate-500/10 text-slate-100' },
    { key: 'queued_total', label: 'Dalam Antrean', value: Number(approvalSummary?.queued_total || 0), tone: 'border-cyan-400/40 bg-cyan-500/10 text-cyan-100' },
    { key: 'approved_total', label: 'Disetujui', value: Number(approvalSummary?.approved_total || 0), tone: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100' },
    { key: 'rejected_total', label: 'Ditolak', value: Number(approvalSummary?.rejected_total || 0), tone: 'border-rose-400/40 bg-rose-500/10 text-rose-100' },
  ]), [approvalSummary]);

  const pendingByEvent = useMemo(() => {
    const rows = approvalSummary?.by_trigger_event && typeof approvalSummary.by_trigger_event === 'object'
      ? Object.entries(approvalSummary.by_trigger_event).map(([eventKey, metrics]) => ({
        eventKey,
        pending: Number(metrics?.pending || 0),
      }))
      : [];
    return rows.filter((row) => row.pending > 0).sort((a, b) => b.pending - a.pending).slice(0, 4);
  }, [approvalSummary]);

  const pendingEventOptions = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const item of pendingOutboundItems) {
      const key = String(item?.trigger_event || '').trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(key);
    }
    return out;
  }, [pendingOutboundItems]);

  const groupedEventOptions = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const item of groupedOutboundItems) {
      const key = String(item?.trigger_event || '').trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(key);
    }
    return out;
  }, [groupedOutboundItems]);

  const filteredGroupedOutboundItems = useMemo(() => {
    const keyword = String(groupSearch || '').trim().toLowerCase();
    const severityRank = { critical: 4, high: 3, medium: 2, low: 1 };
    const rows = groupedOutboundItems.filter((item) => {
      const matchEvent = groupFilterEvent === 'all' || String(item?.trigger_event || '') === groupFilterEvent;
      const matchApproval = groupFilterApprovalStatus === 'all' || String(item?.approval_status || '').toLowerCase() === groupFilterApprovalStatus;
      const matchPendingOnly = !groupPendingOnly || ['pending', 'mixed'].includes(String(item?.approval_status || '').toLowerCase());
      if (!(matchEvent && matchApproval && matchPendingOnly)) return false;
      if (!keyword) return true;
      const haystack = [
        item?.title,
        item?.summary,
        item?.source_type,
        item?.source_id,
        item?.trigger_event,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(keyword);
    });

    const sorted = [...rows];
    if (groupSort === 'urgency') {
      sorted.sort((a, b) => {
        const ap = Number(a?.pending_count || 0);
        const bp = Number(b?.pending_count || 0);
        if (bp !== ap) return bp - ap;
        const as = severityRank[String(a?.severity || '').toLowerCase()] || 0;
        const bs = severityRank[String(b?.severity || '').toLowerCase()] || 0;
        if (bs !== as) return bs - as;
        const aa = Number(a?.audience_count || 0);
        const ba = Number(b?.audience_count || 0);
        if (ba !== aa) return ba - aa;
        return String(b?.updated_at || '').localeCompare(String(a?.updated_at || ''));
      });
    } else if (groupSort === 'audience') {
      sorted.sort((a, b) => Number(b?.audience_count || 0) - Number(a?.audience_count || 0));
    } else if (groupSort === 'latest') {
      sorted.sort((a, b) => String(b?.updated_at || '').localeCompare(String(a?.updated_at || '')));
    }
    return sorted;
  }, [groupedOutboundItems, groupFilterApprovalStatus, groupFilterEvent, groupPendingOnly, groupSearch, groupSort]);

  const visiblePendingOutboundItems = useMemo(() => {
    if (pendingFilterEvent === 'all') return pendingOutboundItems;
    return pendingOutboundItems.filter((item) => String(item?.trigger_event || '') === pendingFilterEvent);
  }, [pendingOutboundItems, pendingFilterEvent]);

  const pendingTotalPages = useMemo(() => {
    if (pendingPageSize <= 0) return 1;
    return Math.max(1, Math.ceil(visiblePendingOutboundItems.length / pendingPageSize));
  }, [visiblePendingOutboundItems.length, pendingPageSize]);

  const paginatedPendingOutboundItems = useMemo(() => {
    const start = (pendingPage - 1) * pendingPageSize;
    return visiblePendingOutboundItems.slice(start, start + pendingPageSize);
  }, [pendingPage, pendingPageSize, visiblePendingOutboundItems]);

  useEffect(() => {
    setSelectedPendingIds((prev) => prev.filter((id) => visiblePendingOutboundItems.some((item) => item.id === id)));
    setPendingPage(1);
  }, [visiblePendingOutboundItems, pendingFilterEvent, pendingPageSize]);

  useEffect(() => {
    if (pendingPage > pendingTotalPages) {
      setPendingPage(pendingTotalPages);
    }
  }, [pendingPage, pendingTotalPages]);

  const processOutboundNow = async () => {
    setProcessingOutbound(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/scheduler/run-whatsapp-outbound-now`, {}, { headers: getHeaders() });
      toast.success(data?.message || 'Outbound berhasil diproses');
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal memproses outbound');
    } finally {
      setProcessingOutbound(false);
    }
  };

  const approvePendingItem = async (messageId) => {
    setActingMessageId(messageId);
    setError('');
    try {
      await axios.post(`${API}/whatsapp/outbound/${messageId}/approve`, {}, { headers: getHeaders() });
      toast.success('Outbound disetujui');
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal menyetujui outbound');
    } finally {
      setActingMessageId(null);
    }
  };

  const actOnGroup = async (groupId, action) => {
    setActingGroupId(groupId);
    setError('');
    try {
      await axios.post(`${API}/whatsapp/outbound/groups/${groupId}/${action}`, {}, { headers: getHeaders() });
      if (action === 'approve') toast.success('Grup disetujui');
      if (action === 'reject') toast.success('Grup ditolak');
      if (action === 'queue') toast.success('Grup masuk antrean');
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Aksi grup gagal');
    } finally {
      setActingGroupId(null);
    }
  };

  const toggleGroupRecipients = async (groupId) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(groupId);
    if (groupRecipientsById[groupId]) return;
    try {
      const { data } = await axios.get(`${API}/whatsapp/outbound/groups/${groupId}/recipients?limit=20&offset=0`, { headers: getHeaders() });
      setGroupRecipientsById((prev) => ({ ...prev, [groupId]: data }));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal memuat penerima grup');
    }
  };

  const rejectPendingItem = async (messageId) => {
    setActingMessageId(messageId);
    setError('');
    try {
      await axios.post(`${API}/whatsapp/outbound/${messageId}/reject?reason=Ditolak%20operator`, {}, { headers: getHeaders() });
      toast.success('Outbound ditolak');
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal menolak outbound');
    } finally {
      setActingMessageId(null);
    }
  };

  const togglePendingSelection = (messageId, checked) => {
    setSelectedPendingIds((prev) => {
      if (checked) {
        if (prev.includes(messageId)) return prev;
        return [...prev, messageId];
      }
      return prev.filter((id) => id !== messageId);
    });
  };

  const toggleSelectAllVisible = (checked) => {
    if (!checked) {
      setSelectedPendingIds((prev) => prev.filter((id) => !paginatedPendingOutboundItems.some((item) => item.id === id)));
      return;
    }
    const visibleIds = paginatedPendingOutboundItems.map((item) => item.id);
    setSelectedPendingIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const approveSelectedPending = async () => {
    if (selectedPendingIds.length === 0) {
      toast.error('Pilih minimal 1 item untuk disetujui');
      return;
    }
    const ok = window.confirm(`Setujui ${selectedPendingIds.length} outbound terpilih?`);
    if (!ok) return;
    setBulkApproving(true);
    setError('');
    try {
      for (const id of selectedPendingIds) {
        // sequential to keep API pressure predictable
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/whatsapp/outbound/${id}/approve`, {}, { headers: getHeaders() });
      }
      toast.success(`${selectedPendingIds.length} outbound disetujui`);
      setSelectedPendingIds([]);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal approve sebagian outbound');
    } finally {
      setBulkApproving(false);
    }
  };

  const rejectSelectedPending = async () => {
    if (selectedPendingIds.length === 0) {
      toast.error('Pilih minimal 1 item untuk ditolak');
      return;
    }
    const ok = window.confirm(`Tolak ${selectedPendingIds.length} outbound terpilih?`);
    if (!ok) return;
    setBulkRejecting(true);
    setError('');
    try {
      for (const id of selectedPendingIds) {
        // sequential to keep API pressure predictable
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/whatsapp/outbound/${id}/reject?reason=Ditolak%20operator`, {}, { headers: getHeaders() });
      }
      toast.success(`${selectedPendingIds.length} outbound ditolak`);
      setSelectedPendingIds([]);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal reject sebagian outbound');
    } finally {
      setBulkRejecting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050a14]">
      {loading ? <StaffPageLoadingOverlay text="Memuat Manajer Template WhatsApp..." /> : null}
      <AppShell>
      <PageContainer>
        <StaffPageHeader
          eyebrow="INTEGRASI WHATSAPP"
          title="Manajer Template WhatsApp"
          subtitle="Kelola template notifikasi berbasis event, validasi variabel, pratinjau, dan uji kirim."
          actions={(
            <>
              <Button variant="outline" className="border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={loadTemplates}>
                <RefreshCw className="mr-2 h-4 w-4" />Muat Ulang
              </Button>
              <Button onClick={onCreateNew} className="bg-cyan-500 text-[#02111f] hover:bg-cyan-400">
                Template Baru
              </Button>
            </>
          )}
        />

        {error ? (
          <div className="mt-4 rounded-xl border border-red-500/45 bg-red-500/10 p-3 text-sm text-red-200">
            {error}
          </div>
        ) : null}

        <section className="mt-4 flex flex-wrap gap-2">
          {CATEGORY_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveCategory(tab.key)}
              className={`rounded-full border px-3 py-1.5 text-xs ${
                activeCategory === tab.key
                  ? 'border-cyan-400/60 bg-cyan-500/15 text-cyan-100'
                  : 'border-white/15 bg-[#0b1222] text-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </section>

        <section className="mt-4 rounded-2xl border border-white/10 bg-[#081121] p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-white">Ringkasan Persetujuan Outbound</h2>
              <p className="mt-1 text-xs text-slate-400">
                Pantau antrean notifikasi yang butuh persetujuan sebelum dikirim.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-cyan-400/35 bg-cyan-500/10 text-cyan-100"
              onClick={processOutboundNow}
              disabled={processingOutbound}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              {processingOutbound ? 'Memproses...' : 'Proses Outbound Sekarang'}
            </Button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {summaryCards.map((card) => (
              <div key={card.key} className={`rounded-xl border p-3 ${card.tone}`}>
                <p className="text-[11px] uppercase tracking-wide opacity-85">{card.label}</p>
                <p className="mt-1 text-2xl font-semibold">{card.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-xl border border-white/10 bg-[#0b1222] p-3">
            <p className="text-xs font-semibold text-slate-200">Menunggu per Event</p>
            {pendingByEvent.length === 0 ? (
              <p className="mt-1 text-xs text-slate-400">Tidak ada event yang menunggu persetujuan saat ini.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                {pendingByEvent.map((row) => (
                  <span key={row.eventKey} className="inline-flex items-center rounded-full border border-amber-400/30 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-100">
                    {getTriggerEventLabel(row.eventKey)}: {row.pending}
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="mt-4 rounded-2xl border border-white/10 bg-[#081121] p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-white">Approval Inbox (Outbound)</h2>
              <p className="mt-1 text-xs text-slate-400">
                Tinjau cepat draft outbound yang menunggu persetujuan.
              </p>
              <p className="mt-1 text-[11px] text-slate-500">
                Terakhir diperbarui: {lastUpdatedAt ? lastUpdatedAt.toLocaleString('id-ID') : '-'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex rounded-full border border-amber-400/35 bg-amber-500/10 px-2.5 py-1 text-xs text-amber-100">
                Menunggu: {pendingOutboundItems.length}
              </span>
              <div className="inline-flex rounded-full border border-white/15 bg-[#0b1222] p-1">
                <button
                  type="button"
                  className={`rounded-full px-2.5 py-1 text-xs ${inboxView === 'grouped' ? 'bg-cyan-500/20 text-cyan-100' : 'text-slate-300'}`}
                  onClick={() => setInboxView('grouped')}
                >
                Digrup
                </button>
                <button
                  type="button"
                  className={`rounded-full px-2.5 py-1 text-xs ${inboxView === 'raw' ? 'bg-cyan-500/20 text-cyan-100' : 'text-slate-300'}`}
                  onClick={() => setInboxView('raw')}
                >
                  Raw
                </button>
              </div>
              <label className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs text-cyan-100">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                Pembaruan otomatis 30 dtk
              </label>
              {inboxView === 'raw' ? (
                <>
                  <Button
                    size="sm"
                    className="bg-emerald-500 text-[#02111f] hover:bg-emerald-400"
                    disabled={bulkApproving || selectedPendingIds.length === 0}
                    onClick={approveSelectedPending}
                  >
                    {bulkApproving ? 'Memproses...' : `Approve Selected (${selectedPendingIds.length})`}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-rose-400/40 bg-rose-500/10 text-rose-100"
                    disabled={bulkRejecting || selectedPendingIds.length === 0}
                    onClick={rejectSelectedPending}
                  >
                    {bulkRejecting ? 'Memproses...' : `Reject Selected (${selectedPendingIds.length})`}
                  </Button>
                </>
              ) : null}
            </div>
          </div>
          {inboxView === 'grouped' ? (
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-slate-300">
                  Cari grup
                  <input
                    className="ml-2 w-48 rounded-lg border border-white/20 bg-[#0b1222] px-2 py-1 text-xs text-white"
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    placeholder="judul/sumber/event"
                  />
                </label>
                <label className="text-xs text-slate-300">
                  Filter event
                  <select
                    className="ml-2 rounded-lg border border-white/20 bg-[#0b1222] px-2 py-1 text-xs text-white"
                    value={groupFilterEvent}
                    onChange={(e) => setGroupFilterEvent(e.target.value)}
                  >
                    <option value="all">Semua Event</option>
                    {groupedEventOptions.map((eventKey) => (
                      <option key={eventKey} value={eventKey}>
                        {getTriggerEventLabel(eventKey)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-slate-300">
                  Status persetujuan
                  <select
                    className="ml-2 rounded-lg border border-white/20 bg-[#0b1222] px-2 py-1 text-xs text-white"
                    value={groupFilterApprovalStatus}
                    onChange={(e) => setGroupFilterApprovalStatus(e.target.value)}
                  >
                    <option value="all">Semua Status</option>
                    <option value="pending">Menunggu</option>
                    <option value="mixed">Campuran</option>
                      <option value="approved">Disetujui</option>
                      <option value="rejected">Ditolak</option>
                    <option value="not_required">Tidak Perlu</option>
                  </select>
                </label>
                <label className="text-xs text-slate-300">
                  Urutan
                  <select
                    className="ml-2 rounded-lg border border-white/20 bg-[#0b1222] px-2 py-1 text-xs text-white"
                    value={groupSort}
                    onChange={(e) => setGroupSort(e.target.value)}
                  >
                    <option value="urgency">Urgensi</option>
                    <option value="latest">Terbaru</option>
                    <option value="audience">Audiens Terbesar</option>
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => setGroupPendingOnly((v) => !v)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    groupPendingOnly
                      ? 'border-amber-400/40 bg-amber-500/10 text-amber-100'
                      : 'border-white/20 bg-[#0b1222] text-slate-300'
                  }`}
                >
                      Hanya menunggu review
                </button>
                <span className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-500/10 px-2.5 py-1 text-xs text-cyan-100">
                      Grup tampil: {filteredGroupedOutboundItems.length}
                </span>
              </div>
              {filteredGroupedOutboundItems.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/20 bg-[#0b1222]/70 p-4 text-center text-xs text-slate-300">
                        Tidak ada grup outbound untuk ditinjau.
                </div>
              ) : filteredGroupedOutboundItems.map((group) => (
                <div key={group.id} className="rounded-xl border border-white/10 bg-[#0b1222] p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-500/10 px-2 py-0.5 text-[11px] text-cyan-100">
                            {group.group_type || 'grup'}
                        </span>
                        <span className="inline-flex rounded-full border border-amber-400/35 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-100">
                          {group.approval_status || 'pending'}
                        </span>
                        <span className="inline-flex rounded-full border border-slate-500/40 bg-slate-600/10 px-2 py-0.5 text-[11px] text-slate-200">
                          {getTriggerEventLabel(group.trigger_event)}
                        </span>
                      </div>
                            <p className="text-sm font-semibold text-white">{group.title || `Grup #${group.id}`}</p>
                      <p className="text-xs text-slate-400">
                              Sumber: {group.source_type} / {group.source_id} � Audiens: {group.audience_count || 0}
                      </p>
                      <p className="text-xs text-slate-400">
                              Menunggu {group.pending_count || 0} � Disetujui {group.approved_count || 0} � Ditolak {group.rejected_count || 0} � Terkirim {group.sent_count || 0}
                      </p>
                      <p className="line-clamp-2 text-xs text-slate-300">{group.sample_message || group.summary || '-'}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-slate-100"
                        onClick={() => toggleGroupRecipients(group.id)}
                      >
                              {expandedGroupId === group.id ? 'Sembunyikan Penerima' : 'Lihat Penerima'}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-500 text-[#02111f] hover:bg-emerald-400"
                        disabled={actingGroupId === group.id}
                        onClick={() => actOnGroup(group.id, 'approve')}
                      >
                              {actingGroupId === group.id ? 'Memproses...' : 'Setujui Grup'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-rose-400/40 bg-rose-500/10 text-rose-100"
                        disabled={actingGroupId === group.id}
                        onClick={() => actOnGroup(group.id, 'reject')}
                      >
                            Tolak Grup
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-cyan-400/40 bg-cyan-500/10 text-cyan-100"
                        disabled={actingGroupId === group.id}
                        onClick={() => actOnGroup(group.id, 'queue')}
                      >
                            Antrekan Grup
                      </Button>
                    </div>
                  </div>
                  {expandedGroupId === group.id ? (
                    <div className="mt-3 rounded-lg border border-white/10 bg-[#0a101e] p-2">
                              <p className="text-[11px] uppercase tracking-wide text-slate-400">Pratinjau Penerima</p>
                      <div className="mt-2 space-y-1">
                        {Array.isArray(groupRecipientsById[group.id]?.recipients) && groupRecipientsById[group.id].recipients.length > 0 ? (
                          groupRecipientsById[group.id].recipients.map((rec) => (
                            <div key={rec.message_id} className="flex items-center justify-between rounded-md border border-white/10 bg-[#0b1222] px-2 py-1 text-xs text-slate-200">
                              <span>{rec.phone_number}</span>
                              <span className="text-slate-400">{rec.status} / {rec.approval_status}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-slate-400">Memuat atau belum ada penerima.</p>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label className="text-xs text-slate-300">
                  Filter event
                  <select
                    className="ml-2 rounded-lg border border-white/20 bg-[#0b1222] px-2 py-1 text-xs text-white"
                    value={pendingFilterEvent}
                    onChange={(e) => setPendingFilterEvent(e.target.value)}
                  >
                    <option value="all">Semua Event</option>
                    {pendingEventOptions.map((eventKey) => (
                      <option key={eventKey} value={eventKey}>
                        {getTriggerEventLabel(eventKey)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#0b1222] px-3 py-1 text-xs text-slate-200">
                  <input
                    type="checkbox"
                    checked={paginatedPendingOutboundItems.length > 0 && paginatedPendingOutboundItems.every((item) => selectedPendingIds.includes(item.id))}
                    onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                  />
                  Pilih semua di halaman ini
                </label>
                <label className="text-xs text-slate-300">
                  Item / halaman
                  <select
                    className="ml-2 rounded-lg border border-white/20 bg-[#0b1222] px-2 py-1 text-xs text-white"
                    value={pendingPageSize}
                    onChange={(e) => setPendingPageSize(Number(e.target.value))}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                  </select>
                </label>
              </div>
              <div className="mt-3 space-y-2">
                {paginatedPendingOutboundItems.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-white/20 bg-[#0b1222]/70 p-4 text-center text-xs text-slate-300">
                    Tidak ada outbound yang menunggu persetujuan.
                  </div>
                ) : paginatedPendingOutboundItems.map((item) => (
                  <div key={item.id} className="rounded-xl border border-white/10 bg-[#0b1222] p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#0a101e] px-2 py-0.5 text-[11px] text-slate-200">
                            <input
                              type="checkbox"
                              checked={selectedPendingIds.includes(item.id)}
                              onChange={(e) => togglePendingSelection(item.id, e.target.checked)}
                            />
                            Pilih
                          </label>
                          <span className="inline-flex rounded-full border border-amber-400/35 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-100">
                            Menunggu Persetujuan
                          </span>
                          <span className="inline-flex rounded-full border border-slate-500/40 bg-slate-600/10 px-2 py-0.5 text-[11px] text-slate-200">
                            {getTriggerEventLabel(item.trigger_event)}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-white">{item.phone_number}</p>
                              <p className="text-xs text-slate-400">Sumber: {item.source_type} / {item.source_id}</p>
                        <p className="line-clamp-2 text-xs text-slate-300">{item.rendered_message}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-500 text-[#02111f] hover:bg-emerald-400"
                          disabled={actingMessageId === item.id}
                          onClick={() => approvePendingItem(item.id)}
                        >
                          {actingMessageId === item.id ? 'Memproses...' : 'Approve'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-rose-400/40 bg-rose-500/10 text-rose-100"
                          disabled={actingMessageId === item.id}
                          onClick={() => rejectPendingItem(item.id)}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between rounded-xl border border-white/10 bg-[#0b1222] px-3 py-2 text-xs text-slate-300">
                <span>
                  Halaman {pendingPage} dari {pendingTotalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 border-white/20 text-slate-100"
                    disabled={pendingPage <= 1}
                    onClick={() => setPendingPage((p) => Math.max(1, p - 1))}
                  >
                    Sebelumnya
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 border-white/20 text-slate-100"
                    disabled={pendingPage >= pendingTotalPages}
                    onClick={() => setPendingPage((p) => Math.min(pendingTotalPages, p + 1))}
                  >
                    Berikutnya
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>

        <section className="mt-5 grid gap-5 xl:grid-cols-[380px,1fr]">
          <div className="rounded-2xl border border-white/10 bg-[#081121] p-4">
            <h2 className="text-sm font-semibold text-white">Daftar Template</h2>
            <div className="mt-3 space-y-2">
              {filteredTemplates.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/20 bg-[#0b1222]/70 p-4 text-center text-xs text-slate-300">
                  Belum ada template di kategori ini.
                </div>
              ) : filteredTemplates.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => onSelectTemplate(row)}
                  className={`w-full rounded-xl border p-3 text-left ${selectedId === row.id ? 'border-cyan-400/55 bg-cyan-500/10' : 'border-white/10 bg-[#0b1222]'}`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] ${statusChipClass(Boolean(row.is_active))}`}>
                      {row.is_active ? 'Aktif' : 'Nonaktif'}
                    </span>
                    <span className="inline-flex rounded-full border border-slate-500/40 bg-slate-600/10 px-2 py-0.5 text-[11px] text-slate-200">
                      {getRowTriggerEventLabel(row)}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">{row.trigger_event}</p>
                  <p className="mt-1 text-sm font-semibold text-white">{row.template_name}</p>
                  <p className="line-clamp-2 text-xs text-slate-300">{row.content}</p>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 border-white/20 text-slate-100"
                      onClick={(e) => { e.stopPropagation(); toggleActive(row, !row.is_active); }}
                    >
                      {row.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                    </Button>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-[#081121] p-4">
              <h2 className="text-sm font-semibold text-white">Editor Template</h2>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="text-xs text-slate-300">
                  Nama Template
                  <input className="mt-1 w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white" value={form.template_name} onChange={(e) => setForm((p) => ({ ...p, template_name: e.target.value }))} />
                </label>
                <label className="text-xs text-slate-300">
                  Template Key
                  <input disabled={Boolean(form.id)} className="mt-1 w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white disabled:opacity-60" value={form.template_key} onChange={(e) => setForm((p) => ({ ...p, template_key: e.target.value }))} />
                </label>
                <label className="text-xs text-slate-300">
                  Kategori
                  <select className="mt-1 w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                    {CATEGORY_TABS.map((tab) => <option key={tab.key} value={tab.key}>{tab.label}</option>)}
                  </select>
                </label>
                <label className="text-xs text-slate-300">
                  Trigger Event
                  <select
                    className="mt-1 w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white"
                    value={form.trigger_event}
                    onChange={(e) => setForm((p) => ({ ...p, trigger_event: e.target.value }))}
                  >
                    <option value="">Pilih trigger event</option>
                    {triggerEventOptionsByCategory.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.label}
                      </option>
                    ))}
                    {form.trigger_event && !triggerEventOptionsByCategory.some((item) => item.key === form.trigger_event) ? (
                      <option value={form.trigger_event}>
                        {getTriggerEventLabel(form.trigger_event)}
                      </option>
                    ) : null}
                  </select>
                </label>
                <label className="text-xs text-slate-300">
                  Channel
                  <select className="mt-1 w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white" value={form.channel} onChange={(e) => setForm((p) => ({ ...p, channel: e.target.value }))}>
                    <option value="whatsapp_business">WhatsApp Business</option>
                    <option value="whatsapp_personal">WhatsApp Personal</option>
                    <option value="internal_test">Internal Test</option>
                  </select>
                </label>
                <label className="text-xs text-slate-300">
                  Sort Order
                  <input type="number" className="mt-1 w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white" value={form.sort_order} onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value || 0) }))} />
                </label>
              </div>

              <label className="mt-3 block text-xs text-slate-300">
                Konten Pesan
                <textarea className="mt-1 min-h-[140px] w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white" value={form.content} onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))} />
              </label>

              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="text-xs text-slate-300">
                  Variabel Diizinkan (pisah koma)
                  <input
                    className="mt-1 w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white"
                    value={form.allowed_variables.join(', ')}
                    onChange={(e) => setForm((p) => ({
                      ...p,
                      allowed_variables: String(e.target.value || '')
                        .split(',')
                        .map((v) => v.trim())
                        .filter(Boolean),
                    }))}
                  />
                </label>
                <label className="text-xs text-slate-300">
                  Catatan
                  <input className="mt-1 w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white" value={form.notes || ''} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
                </label>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-300">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
                  Aktif
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={form.requires_approval} onChange={(e) => setForm((p) => ({ ...p, requires_approval: e.target.checked }))} />
                  Perlu Persetujuan
                </label>
                <span className="inline-flex items-center gap-1 text-slate-400">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {form.is_system_default ? 'Template sistem default' : 'Template kustom'}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={saveTemplate} disabled={saving} className="bg-cyan-500 text-[#02111f] hover:bg-cyan-400">
                  {saving ? 'Menyimpan...' : 'Simpan Template'}
                </Button>
                <Button variant="outline" className="border-white/20 text-slate-100" onClick={validateTemplate} disabled={!form.id}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />Validate
                </Button>
                <Button variant="outline" className="border-white/20 text-slate-100" onClick={testSend}>
                          <Send className="mr-2 h-4 w-4" />Uji Kirim
                </Button>
                {form.is_system_default ? (
                  <Button variant="outline" className="border-amber-500/35 bg-amber-500/10 text-amber-100" onClick={resetDefault} disabled={!form.id}>
                    Reset Default
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-[#081121] p-4">
                <h3 className="text-sm font-semibold text-white">Variable Helper</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {VARIABLE_HELPERS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="rounded-full border border-cyan-400/35 bg-cyan-500/10 px-2.5 py-1 text-[11px] text-cyan-100"
                      onClick={() => insertVariable(key)}
                    >
                      {'{'}{key}{'}'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#081121] p-4">
                <h3 className="text-sm font-semibold text-white">Preview Pesan</h3>
                <p className="mt-2 whitespace-pre-wrap rounded-lg border border-white/10 bg-[#0b1222] p-3 text-sm text-slate-200">
                  {previewText || '-'}
                </p>
                <label className="mt-3 block text-xs text-slate-300">
                  Nomor Test
                  <input className="mt-1 w-full rounded-lg border border-white/20 bg-[#0b1222] px-3 py-2 text-sm text-white" value={testNumber} onChange={(e) => setTestNumber(e.target.value)} />
                </label>
              </div>
            </div>

            {validationResult ? (
              <div className={`rounded-xl border p-3 text-sm ${validationResult.valid ? 'border-emerald-500/45 bg-emerald-500/10 text-emerald-200' : 'border-red-500/45 bg-red-500/10 text-red-200'}`}>
                <div className="flex items-center gap-2">
                  {validationResult.valid ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  <span>{validationResult.message}</span>
                </div>
                {!validationResult.valid && Array.isArray(validationResult.unknown_variables) && validationResult.unknown_variables.length > 0 ? (
                  <p className="mt-2 text-xs">Variabel tidak dikenal: {validationResult.unknown_variables.join(', ')}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
                  </PageContainer>
    </AppShell>
    </div>
  );
}






